import { useEffect, useMemo, useState } from "react";
import { Card, Input, Radio } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

dayjs.extend(utc);

const UNIT_SECONDS = "s";
const UNIT_MILLISECONDS = "ms";

const INTERPRET_LOCAL = "local";
const INTERPRET_UTC = "utc";

const detectUnit = (value) => {
  const trimmed = String(value).trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  const abs = Math.abs(Number(trimmed));
  if (abs >= 1e12) return UNIT_MILLISECONDS;
  return UNIT_SECONDS;
};

const toDayjs = (value, unit) => {
  if (value === "" || value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return null;
  const ms = unit === UNIT_MILLISECONDS ? num : num * 1000;
  const d = dayjs(ms);
  return d.isValid() ? d : null;
};

const formatRelative = (target, t) => {
  if (!target) return "";
  const diffMs = target.valueOf() - Date.now();
  const future = diffMs >= 0;
  const abs = Math.abs(diffMs);
  const sec = Math.round(abs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);

  let value;
  let unitKey;
  if (sec < 60) { value = sec; unitKey = sec === 1 ? "timestamp_converter_unit_second" : "timestamp_converter_unit_seconds"; }
  else if (min < 60) { value = min; unitKey = min === 1 ? "timestamp_converter_unit_minute" : "timestamp_converter_unit_minutes"; }
  else if (hr < 24) { value = hr; unitKey = hr === 1 ? "timestamp_converter_unit_hour" : "timestamp_converter_unit_hours"; }
  else { value = day; unitKey = day === 1 ? "timestamp_converter_unit_day" : "timestamp_converter_unit_days"; }

  const unit = t(unitKey);
  return future
    ? t("timestamp_converter_relative_in", { value, unit })
    : t("timestamp_converter_relative_ago", { value, unit });
};

const TimestampConverter = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(() => String(Math.floor(Date.now() / 1000)));
  const [unit, setUnit] = useState(UNIT_SECONDS);
  const [dateInput, setDateInput] = useState(() =>
    dayjs().format("YYYY-MM-DDTHH:mm:ss")
  );
  const [interpret, setInterpret] = useState(INTERPRET_LOCAL);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const parsed = useMemo(() => toDayjs(input, unit), [input, unit]);

  const dateParsed = useMemo(() => {
    if (!dateInput) return null;
    const d = interpret === INTERPRET_UTC ? dayjs.utc(dateInput) : dayjs(dateInput);
    return d.isValid() ? d : null;
  }, [dateInput, interpret]);

  const useNow = () => {
    const now = Date.now();
    setUnit(UNIT_SECONDS);
    setInput(String(Math.floor(now / 1000)));
    setDateInput(dayjs(now).format("YYYY-MM-DDTHH:mm:ss"));
  };

  const detectAndApplyUnit = () => {
    const detected = detectUnit(input);
    if (detected && detected !== unit) setUnit(detected);
  };

  const copy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const nowMs = Date.now();
  const nowSeconds = Math.floor(nowMs / 1000);

  const outputs = parsed
    ? [
        { key: "iso", labelKey: "timestamp_converter_out_iso", value: parsed.toISOString() },
        { key: "utc", labelKey: "timestamp_converter_out_utc", value: parsed.utc().format("YYYY-MM-DD HH:mm:ss [UTC]") },
        { key: "local", labelKey: "timestamp_converter_out_local", value: parsed.format("YYYY-MM-DD HH:mm:ss Z") },
        { key: "relative", labelKey: "timestamp_converter_out_relative", value: formatRelative(parsed, t) },
      ]
    : [];

  const dateOutputs = dateParsed
    ? [
        { key: "ms", labelKey: "timestamp_converter_out_ms", value: String(dateParsed.valueOf()) },
        { key: "s", labelKey: "timestamp_converter_out_s", value: String(Math.floor(dateParsed.valueOf() / 1000)) },
      ]
    : [];

  // tick is read so re-render captures a fresh "now" value for the live clock display
  void tick;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("timestamp_converter_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("timestamp_converter_intro")}</p>
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs uppercase tracking-wide text-gray-500">
                      {t("timestamp_converter_now_label")}
                    </span>
                    <span className="font-mono text-lg text-gray-800 break-all">
                      {nowSeconds}
                    </span>
                  </div>
                  <StandardButton
                    onClick={() =>
                      copy(String(nowSeconds), t("timestamp_converter_now_label"))
                    }
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs uppercase tracking-wide text-gray-500">
                      {t("timestamp_converter_now_label_ms")}
                    </span>
                    <span className="font-mono text-lg text-gray-800 break-all">
                      {nowMs}
                    </span>
                  </div>
                  <StandardButton
                    onClick={() =>
                      copy(String(nowMs), t("timestamp_converter_now_label_ms"))
                    }
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              <div className="flex justify-end">
                <StandardButton
                  onClick={useNow}
                  type="secondary"
                  variant="outlined"
                  size="middle"
                >
                  {t("timestamp_converter_use_now")}
                </StandardButton>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-lg font-semibold text-gray-800">
                {t("timestamp_converter_ts_to_date_heading")}
              </h2>
              <div className="flex flex-col gap-3 md:flex-row md:items-end">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("timestamp_converter_input_label")}
                  </label>
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onBlur={detectAndApplyUnit}
                    placeholder={t("timestamp_converter_input_placeholder")}
                    className="font-mono"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("timestamp_converter_unit_label")}
                  </label>
                  <Radio.Group value={unit} onChange={(e) => setUnit(e.target.value)}>
                    <Radio.Button value={UNIT_SECONDS}>
                      {t("timestamp_converter_unit_seconds_short")}
                    </Radio.Button>
                    <Radio.Button value={UNIT_MILLISECONDS}>
                      {t("timestamp_converter_unit_milliseconds_short")}
                    </Radio.Button>
                  </Radio.Group>
                </div>
              </div>

              {input.trim() !== "" && !parsed && (
                <p className="text-sm text-red-600">
                  {t("timestamp_converter_invalid_timestamp")}
                </p>
              )}

              {parsed && (
                <ul className="flex flex-col divide-y divide-indigo-100 bg-white border border-indigo-100 rounded-md">
                  {outputs.map((row) => (
                    <li key={row.key} className="flex items-center justify-between gap-3 px-3 py-2">
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-gray-500">
                          {t(row.labelKey)}
                        </span>
                        <span className="font-mono text-sm break-all text-gray-800">{row.value}</span>
                      </div>
                      <StandardButton
                        onClick={() => copy(row.value, t(row.labelKey))}
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-lg font-semibold text-gray-800">
                {t("timestamp_converter_date_to_ts_heading")}
              </h2>
              <div className="flex flex-col gap-3 md:flex-row md:items-end">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("timestamp_converter_date_input_label")}
                  </label>
                  <input
                    type="datetime-local"
                    step="1"
                    value={dateInput}
                    onChange={(e) => setDateInput(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("timestamp_converter_interpret_label")}
                  </label>
                  <Radio.Group
                    value={interpret}
                    onChange={(e) => setInterpret(e.target.value)}
                  >
                    <Radio.Button value={INTERPRET_LOCAL}>
                      {t("timestamp_converter_interpret_local")}
                    </Radio.Button>
                    <Radio.Button value={INTERPRET_UTC}>
                      {t("timestamp_converter_interpret_utc")}
                    </Radio.Button>
                  </Radio.Group>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {interpret === INTERPRET_UTC
                  ? t("timestamp_converter_interpret_help_utc")
                  : t("timestamp_converter_interpret_help_local")}
              </p>

              {dateInput && !dateParsed && (
                <p className="text-sm text-red-600">
                  {t("timestamp_converter_invalid_date")}
                </p>
              )}

              {dateParsed && (
                <ul className="flex flex-col divide-y divide-indigo-100 bg-white border border-indigo-100 rounded-md">
                  {dateOutputs.map((row) => (
                    <li key={row.key} className="flex items-center justify-between gap-3 px-3 py-2">
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-gray-500">
                          {t(row.labelKey)}
                        </span>
                        <span className="font-mono text-sm break-all text-gray-800">{row.value}</span>
                      </div>
                      <StandardButton
                        onClick={() => copy(row.value, t(row.labelKey))}
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("timestamp_converter_note_units")}</p>
              <p>{t("timestamp_converter_note_local")}</p>
              <p>{t("timestamp_converter_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default TimestampConverter;
